import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Upload,
  Plus,
  Trash2,
  Edit3,
  Download,
  Search,
  IndianRupee,
  Package,
  FileSpreadsheet,
  Percent,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Maximize2,
  History,
  Clock,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  X as XIcon,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { DMYDateInput } from "@/components/ui/dmy-date-picker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to format date as DD-MM-YY
const formatDateDDMMYY = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return "N/A";
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  } catch {
    return "N/A";
  }
};

export default function ClientPriceList({ clientCode, clientName, onClose, isFullPage = false }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [priceList, setPriceList] = useState([]);
  const [markupPercentage, setMarkupPercentage] = useState(0);
  const [hasCustomPriceList, setHasCustomPriceList] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showUploadHistoryDialog, setShowUploadHistoryDialog] = useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showEditItemDialog, setShowEditItemDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);  // NEW: Delete all confirmation dialog
  const [deleteHistoryToo, setDeleteHistoryToo] = useState(false);  // NEW: Option to delete price history
  const [deletingAll, setDeletingAll] = useState(false);  // NEW: Loading state for delete
  const [historyItem, setHistoryItem] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingHistory, setUploadingHistory] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [historyFile, setHistoryFile] = useState(null);
  const historyFileInputRef = useRef(null);
  
  // Manual price history entry
  const [showAddHistory, setShowAddHistory] = useState(false);
  const [newHistoryPrice, setNewHistoryPrice] = useState("");
  const [newHistoryFrom, setNewHistoryFrom] = useState("");
  const [newHistoryTo, setNewHistoryTo] = useState("");
  const [addingHistory, setAddingHistory] = useState(false);
  const [editingHistoryId, setEditingHistoryId] = useState(null);
  const [editHistoryPrice, setEditHistoryPrice] = useState("");
  const [editHistoryFrom, setEditHistoryFrom] = useState("");
  const [editHistoryTo, setEditHistoryTo] = useState("");
  const [savingHistoryEdit, setSavingHistoryEdit] = useState(false);
  
  // Form state for add/edit
  const [formData, setFormData] = useState({
    product_name: "",
    generic_name: "",
    company: "",
    unit: "",
    sell_price: "",
    is_branded: false,
    alias_1: "",
    alias_2: "",
    alias_3: "",
    alias_4: "",
    alias_5: ""
  });

  // Fetch price list
  const fetchPriceList = useCallback(async () => {
    if (!clientCode) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/price-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (response.ok) {
        setPriceList(data.price_list || []);
        setMarkupPercentage(data.markup_percentage || 0);
        setHasCustomPriceList(data.has_custom_price_list);
      } else {
        toast.error(data.detail || "Failed to fetch price list");
      }
    } catch (error) {
      console.error("Error fetching price list:", error);
      toast.error("Failed to fetch price list");
    } finally {
      setLoading(false);
    }
  }, [clientCode, token]);

  useEffect(() => {
    fetchPriceList();
  }, [fetchPriceList]);

  // Fetch price history for an item
  const fetchPriceHistory = async (item) => {
    setHistoryItem(item);
    setShowHistoryDialog(true);
    setLoadingHistory(true);
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/ledgers/${clientCode}/price-list/${item.id}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      
      if (response.ok) {
        const history = data.price_history || [];
        setPriceHistory(history);
        
        // Calculate effective current price based on today's date
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        let effectivePrice = item.sell_price; // Default to stored price
        let effectiveRecord = null;
        
        for (const h of history) {
          const fromDate = h.effective_from ? new Date(h.effective_from) : null;
          const toDate = h.effective_to ? new Date(h.effective_to) : null;
          
          if (fromDate) fromDate.setHours(0, 0, 0, 0);
          if (toDate) toDate.setHours(23, 59, 59, 999);
          
          // Check if today falls within this date range
          const afterFrom = !fromDate || today >= fromDate;
          const beforeTo = !toDate || today <= toDate;
          
          if (afterFrom && beforeTo) {
            effectivePrice = h.sell_price;
            effectiveRecord = h;
            break; // First matching record (most recent)
          }
        }
        
        // Update historyItem with effective price
        setHistoryItem({
          ...item,
          effective_sell_price: effectivePrice,
          effective_from: effectiveRecord?.effective_from,
          effective_to: effectiveRecord?.effective_to,
          is_from_history: !!effectiveRecord
        });
      } else {
        toast.error("Failed to fetch price history");
        setPriceHistory([]);
      }
    } catch (error) {
      console.error("Error fetching price history:", error);
      toast.error("Failed to fetch price history");
      setPriceHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Add manual price history entry
  const handleAddManualHistory = async () => {
    if (!historyItem || !newHistoryPrice || !newHistoryFrom) {
      toast.error("Price and Effective From date are required");
      return;
    }
    try {
      setAddingHistory(true);
      const response = await fetch(
        `${BACKEND_URL}/api/ledgers/${clientCode}/price-list/${historyItem.id}/history`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            sell_price: parseFloat(newHistoryPrice),
            effective_from: newHistoryFrom,
            effective_to: newHistoryTo || null
          })
        }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success("Price history entry added");
        setShowAddHistory(false);
        setNewHistoryPrice("");
        setNewHistoryFrom("");
        setNewHistoryTo("");
        fetchPriceHistory(historyItem);
      } else {
        toast.error(data.detail || "Failed to add entry");
      }
    } catch (error) {
      toast.error("Failed to add entry");
    } finally {
      setAddingHistory(false);
    }
  };

  // Edit existing price history entry
  const handleEditHistoryEntry = async (entryId) => {
    if (!editHistoryPrice) { toast.error("Price is required"); return; }
    try {
      setSavingHistoryEdit(true);
      const response = await fetch(
        `${BACKEND_URL}/api/ledgers/${clientCode}/price-history/${entryId}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            sell_price: parseFloat(editHistoryPrice),
            effective_from: editHistoryFrom || null,
            effective_to: editHistoryTo || null
          })
        }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success("Price history entry updated");
        setEditingHistoryId(null);
        fetchPriceHistory(historyItem);
      } else {
        toast.error(data.detail || "Failed to update");
      }
    } catch { toast.error("Failed to update"); }
    finally { setSavingHistoryEdit(false); }
  };

  // Helper to convert ISO date to input date format
  const isoToInputDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toISOString().split("T")[0]; } catch { return ""; }
  };

  // Download price history template
  const handleDownloadHistoryTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/client-price-history/template?client_code=${encodeURIComponent(clientCode)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${clientCode}_price_history_template.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("Template downloaded");
      } else {
        toast.error("Failed to download template");
      }
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Failed to download template");
    }
  };

  // Upload price history file
  const handleUploadHistory = async () => {
    if (!historyFile) {
      toast.error("Please select a file");
      return;
    }
    
    setUploadingHistory(true);
    
    try {
      const formData = new FormData();
      formData.append("file", historyFile);
      
      const response = await fetch(
        `${BACKEND_URL}/api/ledgers/${clientCode}/price-history/upload`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        const addedMsg = data.added_count > 0 ? `${data.added_count} new entries added` : '';
        const updatedMsg = data.updated_count > 0 ? `${data.updated_count} existing entries updated (replaced)` : '';
        const newItemsMsg = data.new_items_created > 0 ? `${data.new_items_created} new products added to price list` : '';
        const parts = [addedMsg, updatedMsg, newItemsMsg].filter(Boolean).join(', ');
        toast.success(`Price history uploaded: ${parts || 'No changes'}`);
        if (data.updated_count > 0 && data.added_count === 0) {
          toast.info(`${data.updated_count} prices replaced with new values (same product + same date)`);
        }
        if (data.new_items_created > 0) {
          toast.info(`${data.new_items_created} new products were auto-added to your price list from history`);
        }
        setShowUploadHistoryDialog(false);
        setHistoryFile(null);
        if (historyFileInputRef.current) historyFileInputRef.current.value = "";
      } else {
        toast.error(data.detail || "Failed to upload price history");
      }
    } catch (error) {
      console.error("Error uploading price history:", error);
      toast.error("Failed to upload price history");
    } finally {
      setUploadingHistory(false);
    }
  };

  // Download template Excel file
  const handleDownloadTemplate = () => {
    // Create template data
    const templateData = [
      {
        product_name: "EXAMPLE PRODUCT",
        generic_name: "Generic Name",
        company: "Company Name",
        unit: "10 Tablets",
        sell_price: "100",
        is_branded: "No"
      },
      {
        product_name: "ANOTHER PRODUCT",
        generic_name: "Another Generic",
        company: "Another Company",
        unit: "30 Capsules",
        sell_price: "250",
        is_branded: "Yes"
      }
    ];

    // Create worksheet
    const XLSX = require("xlsx");
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    
    // Set column widths
    worksheet["!cols"] = [
      { wch: 25 }, // product_name
      { wch: 20 }, // generic_name
      { wch: 20 }, // company
      { wch: 15 }, // unit
      { wch: 12 }, // sell_price
      { wch: 12 }  // is_branded
    ];
    
    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Price List Template");
    
    // Download file
    XLSX.writeFile(workbook, `Price_List_Template_${clientCode}.xlsx`);
    toast.success("Template downloaded successfully");
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const url = `${BACKEND_URL}/api/ledgers/${clientCode}/price-list/upload?replace_existing=${replaceExisting}`;
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Uploaded: ${data.added_count} added, ${data.updated_count} updated`);
        setShowUploadDialog(false);
        fetchPriceList();
      } else {
        toast.error(data.detail || "Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Update markup percentage
  const handleUpdateMarkup = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/markup`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ markup_percentage: markupPercentage })
      });
      
      if (response.ok) {
        toast.success("Markup percentage updated");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update markup");
      }
    } catch (error) {
      console.error("Error updating markup:", error);
      toast.error("Failed to update markup");
    }
  };

  // Add price item
  const handleAddItem = async () => {
    if (!formData.product_name.trim()) {
      toast.error("Product name is required");
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/price-list/item`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          sell_price: parseFloat(formData.sell_price) || 0,
          effective_from: formData.effective_from || null,
          effective_to: formData.effective_to || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        // If dates were provided, also create a price history entry
        if (formData.effective_from && data.item_id) {
          try {
            await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/price-list/${data.item_id}/history`, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                sell_price: parseFloat(formData.sell_price) || 0,
                effective_from: formData.effective_from,
                effective_to: formData.effective_to || null
              })
            });
          } catch (e) { /* history entry is optional */ }
        }
        toast.success("Price item added" + (formData.effective_from ? " with price history" : ""));
        setShowAddItemDialog(false);
        setFormData({ product_name: "", generic_name: "", company: "", unit: "", sell_price: "", is_branded: false, effective_from: "", effective_to: "" });
        fetchPriceList();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to add item");
      }
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error("Failed to add item");
    }
  };

  // Update price item
  const handleUpdateItem = async () => {
    if (!editingItem?.id) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/price-list/${editingItem.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          sell_price: parseFloat(formData.sell_price) || 0
        })
      });
      
      if (response.ok) {
        toast.success("Price item updated");
        setShowEditItemDialog(false);
        setEditingItem(null);
        fetchPriceList();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to update item");
      }
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error("Failed to update item");
    }
  };

  // Delete price item
  const handleDeleteItem = async (itemId) => {
    if (!window.confirm("Delete this price item?")) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/price-list/${itemId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success("Price item deleted");
        fetchPriceList();
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to delete item");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  // Delete entire price list - show confirmation dialog
  const handleDeleteAll = () => {
    setDeleteHistoryToo(false);  // Reset checkbox
    setShowDeleteAllDialog(true);
  };
  
  // Confirm delete all
  const confirmDeleteAll = async () => {
    setDeletingAll(true);
    
    try {
      // Delete price list
      const response = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/price-list`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        
        // Also delete price history if checkbox was checked
        if (deleteHistoryToo) {
          try {
            const historyResponse = await fetch(`${BACKEND_URL}/api/ledgers/${clientCode}/price-history`, {
              method: "DELETE",
              headers: { Authorization: `Bearer ${token}` }
            });
            
            if (historyResponse.ok) {
              const historyData = await historyResponse.json();
              toast.success(historyData.message || "Price history deleted successfully");
            } else {
              const historyError = await historyResponse.json();
              toast.error(historyError.detail || "Failed to delete price history");
            }
          } catch (historyError) {
            console.error("Error deleting price history:", historyError);
            toast.error("Failed to delete price history");
          }
        }
        
        fetchPriceList();
      } else {
        toast.error(data.detail || "Failed to delete price list");
      }
    } catch (error) {
      console.error("Error deleting price list:", error);
      toast.error("Failed to delete price list");
    } finally {
      setDeletingAll(false);
      setShowDeleteAllDialog(false);
    }
  };

  // Filter price list
  const filteredPriceList = priceList.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      item.product_name?.toLowerCase().includes(term) ||
      item.generic_name?.toLowerCase().includes(term) ||
      item.company?.toLowerCase().includes(term)
    );
  });

  // Open edit dialog
  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      product_name: item.product_name || "",
      generic_name: item.generic_name || "",
      company: item.company || "",
      unit: item.unit || "",
      sell_price: item.sell_price || "",
      is_branded: item.is_branded || false,
      alias_1: item.alias_1 || "",
      alias_2: item.alias_2 || "",
      alias_3: item.alias_3 || "",
      alias_4: item.alias_4 || "",
      alias_5: item.alias_5 || ""
    });
    setShowEditItemDialog(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value || 0);
  };

  // Open in new tab handler
  const handleOpenInNewTab = () => {
    const url = `/price-list/${encodeURIComponent(clientCode)}?name=${encodeURIComponent(clientName || "")}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-600" />
            DP CLIENT Price List for {clientCode}
          </h3>
          <p className="text-sm text-muted-foreground">
            {clientName && `(${clientName})`} • {priceList.length} items
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isFullPage && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleOpenInNewTab} 
              className="gap-2"
              title="Open in new tab for better view"
            >
              <ExternalLink className="w-4 h-4" />
              Open in New Tab
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={fetchPriceList} className="gap-2">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleDownloadTemplate()}
            className="gap-2"
            data-testid="download-template-btn"
          >
            <Download className="w-4 h-4" />
            Template
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowUploadDialog(true)} className="gap-2">
            <Upload className="w-4 h-4" />
            Upload Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowUploadHistoryDialog(true)} className="gap-2 text-blue-600 border-blue-300 hover:bg-blue-50">
            <History className="w-4 h-4" />
            Upload History
          </Button>
          <Button size="sm" onClick={() => {
            setFormData({ product_name: "", generic_name: "", company: "", unit: "", sell_price: "", is_branded: false });
            setShowAddItemDialog(true);
          }} className="gap-2">
            <Plus className="w-4 h-4" />
            Add Item
          </Button>
        </div>
      </div>

      {/* Markup Percentage Section */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Percent className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">Markup Percentage</p>
              <p className="text-xs text-amber-600">
                {hasCustomPriceList 
                  ? "Custom price list available. Markup applies to products NOT in the list."
                  : "No custom price list. All products will use: Base Price × (1 + Markup%)"
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={markupPercentage}
              onChange={(e) => setMarkupPercentage(parseFloat(e.target.value) || 0)}
              className="w-24 h-8 text-right"
              step="0.1"
            />
            <span className="text-amber-700 font-medium">%</span>
            <Button size="sm" variant="outline" onClick={handleUpdateMarkup} className="h-8">
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 gap-4">
        <div className={`p-4 rounded-lg border ${hasCustomPriceList ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
          <div className="flex items-center gap-2">
            {hasCustomPriceList ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-slate-400" />
            )}
            <span className={`font-medium ${hasCustomPriceList ? 'text-green-700' : 'text-slate-500'}`}>
              {hasCustomPriceList ? "Custom Price List Active" : "No Custom Price List"}
            </span>
          </div>
          <p className="text-2xl font-bold mt-2">{priceList.length}</p>
          <p className="text-xs text-muted-foreground">Products with custom prices</p>
        </div>
        
        <div className="p-4 rounded-lg border bg-indigo-50 border-indigo-200">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-indigo-600" />
            <span className="font-medium text-indigo-700">Markup Applied</span>
          </div>
          <p className="text-2xl font-bold mt-2">{markupPercentage}%</p>
          <p className="text-xs text-muted-foreground">On base prices without custom rate</p>
        </div>
      </div>

      {/* Search */}
      {priceList.length > 0 && (
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by product, generic name, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          {priceList.length > 0 && (
            <Button variant="ghost" size="sm" onClick={handleDeleteAll} className="text-red-600 hover:bg-red-50 hover:text-red-700">
              <Trash2 className="w-4 h-4 mr-2" />
              Delete All
            </Button>
          )}
        </div>
      )}

      {/* Price List Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : priceList.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 border rounded-lg bg-white">
          <FileSpreadsheet className="w-16 h-16 text-slate-300" />
          <p className="text-slate-500 font-medium mt-4">No custom price list</p>
          <p className="text-sm text-slate-400 mt-1">Upload an Excel file or add items manually</p>
          <Button variant="outline" size="sm" onClick={() => setShowUploadDialog(true)} className="mt-4 gap-2">
            <Upload className="w-4 h-4" />
            Upload Price List
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-100">
                <TableHead className="w-12">#</TableHead>
                <TableHead>Product Name</TableHead>
                <TableHead>Generic Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead className="text-right">Current Price</TableHead>
                <TableHead className="text-center">Branded</TableHead>
                <TableHead className="w-24 text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPriceList.map((item, idx) => (
                <TableRow key={item.id} className="hover:bg-slate-50">
                  <TableCell className="text-slate-500">{idx + 1}</TableCell>
                  <TableCell className="font-medium">{item.product_name}</TableCell>
                  <TableCell className="text-slate-600">{item.generic_name || "-"}</TableCell>
                  <TableCell className="text-slate-600">{item.company || "-"}</TableCell>
                  <TableCell>{item.unit || "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex flex-col items-end">
                      <span className={`font-semibold ${item.is_effective_from_history ? 'text-blue-700' : 'text-green-700'}`}>
                        {formatCurrency(item.effective_price !== undefined ? item.effective_price : item.sell_price)}
                      </span>
                      {item.is_effective_from_history && (
                        <span className="text-xs text-blue-500 flex items-center gap-0.5" title={`Effective: ${formatDateDDMMYY(item.effective_from)} - ${item.effective_to ? formatDateDDMMYY(item.effective_to) : 'Ongoing'}`}>
                          <Clock className="w-3 h-3" />
                          Date-Effective
                        </span>
                      )}
                      {item.is_effective_from_history && item.sell_price !== item.effective_price && (
                        <span className="text-xs text-slate-400">
                          Base: {formatCurrency(item.sell_price)}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {item.is_branded ? (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">Yes</span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => openEditDialog(item)}
                        title="Edit"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          fetchPriceHistory(item);
                        }}
                        title="Price History"
                      >
                        <History className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteItem(item.id)}
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Price History Dialog - Portal to document.body */}
      {createPortal(
        <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
          <DialogContent className="max-w-2xl z-[9999]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-blue-600" />
                Price History - {historyItem?.product_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Effective Current Price - based on today's date */}
              <div className={`rounded-lg p-4 ${historyItem?.is_from_history ? 'bg-blue-50 border border-blue-200' : 'bg-green-50 border border-green-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className={`text-sm font-medium ${historyItem?.is_from_history ? 'text-blue-800' : 'text-green-800'}`}>
                      {historyItem?.is_from_history ? 'Effective Price (Today)' : 'Base Price'}
                    </span>
                    {historyItem?.is_from_history && historyItem?.effective_from && (
                      <p className="text-xs text-blue-600 font-mono mt-1">
                        Valid: {formatDateDDMMYY(historyItem.effective_from)} → {historyItem.effective_to ? formatDateDDMMYY(historyItem.effective_to) : 'Ongoing'}
                      </p>
                    )}
                  </div>
                  <span className={`text-lg font-bold flex items-center gap-1 ${historyItem?.is_from_history ? 'text-blue-700' : 'text-green-700'}`}>
                    <IndianRupee className="w-4 h-4" />
                    {(historyItem?.effective_sell_price || historyItem?.sell_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                  </span>
                </div>
                {historyItem?.is_from_history && historyItem?.sell_price !== historyItem?.effective_sell_price && (
                  <div className="mt-2 pt-2 border-t border-blue-200">
                    <span className="text-xs text-slate-500">Base Price: ₹{(historyItem?.sell_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 1 })}</span>
                  </div>
                )}
              </div>

              {/* History List */}
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <>
                  {/* Add Manual Entry Button */}
                  {!showAddHistory && (
                    <button
                      onClick={() => setShowAddHistory(true)}
                      className="w-full flex items-center justify-center gap-2 py-2 px-3 mb-3 rounded-lg border border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 text-sm font-medium transition-colors"
                      data-testid="add-price-history-btn"
                    >
                      <Plus className="w-4 h-4" /> Add Price History Entry
                    </button>
                  )}

                  {/* Manual Entry Form */}
                  {showAddHistory && (
                    <div className="mb-3 p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-blue-800">New Price Entry</p>
                        <button onClick={() => setShowAddHistory(false)} className="text-slate-400 hover:text-slate-600">
                          <XIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <div>
                        <label className="text-xs text-slate-600">Sell Price (₹) *</label>
                        <input
                          type="number" step="0.01" value={newHistoryPrice}
                          onChange={(e) => setNewHistoryPrice(e.target.value)}
                          className="w-full mt-0.5 px-2 py-1.5 text-sm border rounded-md"
                          placeholder="Enter price"
                          data-testid="manual-history-price"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs text-slate-600">Effective From *</label>
                          <DMYDateInput value={newHistoryFrom} onChange={(e) => setNewHistoryFrom(e.target.value)} className="w-full mt-0.5 px-2 py-1.5 text-sm border rounded-md" data-testid="manual-history-from" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-600">Effective To</label>
                          <DMYDateInput value={newHistoryTo} onChange={(e) => setNewHistoryTo(e.target.value)} className="w-full mt-0.5 px-2 py-1.5 text-sm border rounded-md" data-testid="manual-history-to" />
                        </div>
                      </div>
                      <button
                        onClick={handleAddManualHistory}
                        disabled={addingHistory || !newHistoryPrice || !newHistoryFrom}
                        className="w-full py-1.5 px-3 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                        data-testid="confirm-add-history-btn"
                      >
                        {addingHistory ? "Adding..." : "Add Entry"}
                      </button>
                    </div>
                  )}

                  {priceHistory.length === 0 && !showAddHistory ? (
                    <div className="text-center py-8 text-slate-500">
                      <Clock className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p>No price history recorded yet</p>
                      <p className="text-xs mt-1">Click "Add Price History Entry" above or upload via Excel</p>
                    </div>
                  ) : priceHistory.length > 0 && (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  <p className="text-sm font-medium text-slate-700 mb-2">Previous Prices ({priceHistory.length})</p>
                  {priceHistory.map((h, idx) => (
                    <div
                      key={h.id || idx}
                      className="p-3 bg-slate-50 rounded-lg border"
                    >
                      {editingHistoryId === (h.id || idx) ? (
                        /* Inline Edit Form */
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-amber-700">Edit Price Entry</p>
                            <button onClick={() => setEditingHistoryId(null)} className="text-slate-400 hover:text-slate-600">
                              <XIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500">Sell Price (₹)</label>
                            <input type="number" step="0.01" value={editHistoryPrice}
                              onChange={(e) => setEditHistoryPrice(e.target.value)}
                              className="w-full mt-0.5 px-2 py-1 text-sm border rounded-md" />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-slate-500">From</label>
                              <DMYDateInput value={editHistoryFrom} onChange={(e) => setEditHistoryFrom(e.target.value)} className="w-full mt-0.5 px-2 py-1 text-sm border rounded-md" />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">To</label>
                              <DMYDateInput value={editHistoryTo} onChange={(e) => setEditHistoryTo(e.target.value)} className="w-full mt-0.5 px-2 py-1 text-sm border rounded-md" />
                            </div>
                          </div>
                          <button
                            onClick={() => handleEditHistoryEntry(h.id || idx)}
                            disabled={savingHistoryEdit || !editHistoryPrice}
                            className="w-full py-1 px-2 bg-amber-600 text-white text-xs font-medium rounded-md hover:bg-amber-700 disabled:opacity-50"
                            data-testid="save-history-edit-btn"
                          >
                            {savingHistoryEdit ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      ) : (
                        /* Display Mode */
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-100 rounded-full">
                              <IndianRupee className="w-4 h-4 text-amber-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-slate-700">
                                  ₹{(h.sell_price || 0).toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                                </p>
                                {historyItem?.sell_price && h.sell_price !== historyItem.sell_price && (
                                  <span className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                                    h.sell_price > historyItem.sell_price 
                                      ? 'bg-red-100 text-red-700' 
                                      : 'bg-green-100 text-green-700'
                                  }`}>
                                    {h.sell_price > historyItem.sell_price ? (
                                      <TrendingDown className="w-3 h-3" />
                                    ) : (
                                      <TrendingUp className="w-3 h-3" />
                                    )}
                                    {Math.abs(((historyItem.sell_price - h.sell_price) / h.sell_price) * 100).toFixed(1)}%
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 font-mono">
                                {formatDateDDMMYY(h.effective_from)}
                                <ArrowRight className="w-3 h-3 inline mx-1" />
                                {h.effective_to ? formatDateDDMMYY(h.effective_to) : 'Current'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {h.changed_by && (
                              <span className="text-xs text-slate-400">by {h.changed_by}</span>
                            )}
                            <button
                              onClick={() => {
                                setEditingHistoryId(h.id || idx);
                                setEditHistoryPrice(h.sell_price?.toString() || "");
                                setEditHistoryFrom(isoToInputDate(h.effective_from));
                                setEditHistoryTo(isoToInputDate(h.effective_to));
                              }}
                              className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-amber-600 transition-colors"
                              title="Edit this entry"
                              data-testid={`edit-history-${idx}`}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>,
        document.body
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Upload Price List
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800">Expected Excel Columns:</p>
              <p className="text-xs text-blue-600 mt-1">
                S.No, Product Name, Generic Name, Company, Unit, Sell Price, Branded
              </p>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="replace"
                checked={replaceExisting}
                onCheckedChange={setReplaceExisting}
              />
              <Label htmlFor="replace" className="text-sm">
                Replace existing price list (delete all current items first)
              </Label>
            </div>
            
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
                id="price-file-upload"
                disabled={uploading}
              />
              <label htmlFor="price-file-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400" />
                <p className="mt-2 text-sm font-medium">
                  {uploading ? "Uploading..." : "Click to select Excel file"}
                </p>
                <p className="text-xs text-slate-400 mt-1">.xlsx or .xls</p>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Price History Dialog */}
      <Dialog open={showUploadHistoryDialog} onOpenChange={setShowUploadHistoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Upload Price History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-medium text-blue-800">Expected Excel Columns:</p>
              <p className="text-xs text-blue-600 mt-1">
                product_name, sell_price, effective_from (DD-MM-YYYY), effective_to (DD-MM-YYYY)
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Use this to upload historical prices (last 5 years data)
              </p>
            </div>
            
            <Button variant="outline" size="sm" onClick={handleDownloadHistoryTemplate} className="w-full gap-2">
              <Download className="w-4 h-4" />
              Download History Template
            </Button>
            
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
              <input
                ref={historyFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setHistoryFile(e.target.files[0])}
                className="hidden"
                id="history-file-upload"
                disabled={uploadingHistory}
              />
              <label htmlFor="history-file-upload" className="cursor-pointer">
                <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400" />
                <p className="mt-2 text-sm font-medium">
                  {historyFile ? historyFile.name : "Click to select Excel file"}
                </p>
                <p className="text-xs text-slate-400 mt-1">.xlsx or .xls</p>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUploadHistoryDialog(false);
              setHistoryFile(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleUploadHistory} disabled={uploadingHistory || !historyFile} className="gap-2">
              {uploadingHistory ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploadingHistory ? "Uploading..." : "Upload History"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add Price Item
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product Name *</Label>
              <Input
                value={formData.product_name}
                onChange={(e) => setFormData(f => ({ ...f, product_name: e.target.value }))}
                placeholder="Enter product name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Generic Name</Label>
                <Input
                  value={formData.generic_name}
                  onChange={(e) => setFormData(f => ({ ...f, generic_name: e.target.value }))}
                  placeholder="Generic name"
                />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData(f => ({ ...f, company: e.target.value }))}
                  placeholder="Company/Brand"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input
                  value={formData.unit}
                  onChange={(e) => setFormData(f => ({ ...f, unit: e.target.value }))}
                  placeholder="e.g., 10 TAB"
                />
              </div>
              <div className="space-y-2">
                <Label>Sell Price (₹) *</Label>
                <Input
                  type="number"
                  value={formData.sell_price}
                  onChange={(e) => setFormData(f => ({ ...f, sell_price: e.target.value }))}
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_branded"
                checked={formData.is_branded}
                onCheckedChange={(checked) => setFormData(f => ({ ...f, is_branded: checked }))}
              />
              <Label htmlFor="is_branded">Branded Product</Label>
            </div>
            <div className="border-t pt-3 mt-1">
              <p className="text-xs font-medium text-slate-500 mb-2">Price History (Optional)</p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Effective From</Label>
                  <DMYDateInput
  value={formData.effective_from || ""}
                    onChange={(e) => setFormData(f => ({ ...f, effective_from: e.target.value }))}
                    data-testid="add-item-effective-from"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Effective To</Label>
                  <DMYDateInput
  value={formData.effective_to || ""}
                    onChange={(e) => setFormData(f => ({ ...f, effective_to: e.target.value }))}
                    data-testid="add-item-effective-to"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>Cancel</Button>
            <Button onClick={handleAddItem}>Add Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={showEditItemDialog} onOpenChange={setShowEditItemDialog}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="w-5 h-5" />
              Edit Price Item
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product Name *</Label>
              <Input
                value={formData.product_name}
                onChange={(e) => setFormData(f => ({ ...f, product_name: e.target.value }))}
                placeholder="Enter product name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Generic Name</Label>
                <Input
                  value={formData.generic_name}
                  onChange={(e) => setFormData(f => ({ ...f, generic_name: e.target.value }))}
                  placeholder="Generic name"
                />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData(f => ({ ...f, company: e.target.value }))}
                  placeholder="Company/Brand"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input
                  value={formData.unit}
                  onChange={(e) => setFormData(f => ({ ...f, unit: e.target.value }))}
                  placeholder="e.g., 10 TAB"
                />
              </div>
              <div className="space-y-2">
                <Label>Sell Price (₹) *</Label>
                <Input
                  type="number"
                  value={formData.sell_price}
                  onChange={(e) => setFormData(f => ({ ...f, sell_price: e.target.value }))}
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit_is_branded"
                checked={formData.is_branded}
                onCheckedChange={(checked) => setFormData(f => ({ ...f, is_branded: checked }))}
              />
              <Label htmlFor="edit_is_branded">Branded Product</Label>
            </div>
            
            {/* Alias Names Section */}
            <div className="pt-4 border-t">
              <Label className="text-sm font-medium text-slate-700 mb-3 block">
                Alias Names (for auto-matching different product names)
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Alias 1</Label>
                  <Input
                    value={formData.alias_1}
                    onChange={(e) => setFormData(f => ({ ...f, alias_1: e.target.value }))}
                    placeholder="e.g., RENOCEL 4000"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Alias 2</Label>
                  <Input
                    value={formData.alias_2}
                    onChange={(e) => setFormData(f => ({ ...f, alias_2: e.target.value }))}
                    placeholder="Alternative name"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Alias 3</Label>
                  <Input
                    value={formData.alias_3}
                    onChange={(e) => setFormData(f => ({ ...f, alias_3: e.target.value }))}
                    placeholder="Alternative name"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Alias 4</Label>
                  <Input
                    value={formData.alias_4}
                    onChange={(e) => setFormData(f => ({ ...f, alias_4: e.target.value }))}
                    placeholder="Alternative name"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Alias 5</Label>
                  <Input
                    value={formData.alias_5}
                    onChange={(e) => setFormData(f => ({ ...f, alias_5: e.target.value }))}
                    placeholder="Alternative name"
                    className="h-8 text-sm"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                Add alternative names that should map to this product during upload matching
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditItemDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateItem}>Update Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete All Price Items
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                You are about to delete <strong>{priceList.length} price items</strong> for <strong>{clientCode}</strong>.
              </p>
              <p className="text-sm text-red-600 mt-2">
                This action cannot be undone.
              </p>
            </div>
            
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <Checkbox
                id="delete_history_too"
                checked={deleteHistoryToo}
                onCheckedChange={setDeleteHistoryToo}
                className="mt-0.5"
              />
              <div>
                <Label htmlFor="delete_history_too" className="text-sm font-medium text-amber-900 cursor-pointer">
                  Also delete price history
                </Label>
                <p className="text-xs text-amber-700 mt-1">
                  This will permanently remove all uploaded price history records for this client.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteAllDialog(false)}
              disabled={deletingAll}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteAll}
              disabled={deletingAll}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingAll ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete {deleteHistoryToo ? "All + History" : "All Items"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
